import { AxiosProgressEvent } from 'axios';

import CommunityAPI from '../../api/CommunityAPI';
import { isChannelLocked } from '../../shared/communityUtils';
import showAppError from '../../shared/error';
import { getGenericFileType } from '../../shared/fileUtils';
import { IChannel, ICommunityUploadedMedia } from '../../types/communityTypes';
import { UAMPermissions } from '../../types/userTypes';
import { setUserCommunityAccess } from '../communityReducer';
import { AppThunk } from '../store';
import { checkForPermissions } from '../userReducer';
import { EUploadModuleType } from '../../types/fileTypes';

export const updateUserCommunityAccess =
  (): AppThunk => async (dispatch, getState) => {
    const {
      isTagMango,
      badges: allBadges,
      isCommunityEnabled,
      hostMetadata,
    } = getState().app;
    const {
      id: userId,
      type: userType,
      userAccessPermissions,
      badges: userBadges,
      isBlockedFromCommunityEngagement,
    } = getState().user;
    const { allCreatorMangoes, subscribedMangoes } = getState().mango;
    const { groupChannelList } = getState().community;

    // user has access to the community if they are a creator or have the required permissions as UAM
    const hasCommunityCreatorAccess =
      userType === 'creator_completed' ||
      (userType === 'creator_restricted' &&
        checkForPermissions(
          [UAMPermissions.COMMUNITY_ACCESS],
          userAccessPermissions,
        ));

    // user can post in mangoes if they are a creator or have the required permissions as UAM or customer not blocked from community engagement and has access to community enabled mangoes (hostData.communityEnabledMangoes) for creating posts
    const canPostInMangoes = Boolean(
      (isTagMango
        ? userType === 'creator_completed' || userType === 'dual'
        : userId === hostMetadata.creator._id) ||
      (userType === 'creator_restricted' &&
        checkForPermissions(
          [UAMPermissions.FEED_WRITE],
          userAccessPermissions,
        )) ||
      (userType === 'fan_completed' &&
        isCommunityEnabled &&
        !isBlockedFromCommunityEngagement),
    );

    // user can post in a channel if they have any of the required badges or mangoes for that channel or they have creator access
    const accessChannelsToPost: IChannel[] = [];
    const canPostInChannels =
      hasCommunityCreatorAccess ||
      groupChannelList.reduce((acc, group) => {
        // filter channels only if it has feed enabled and allowMemberPosts set to true and is not locked for the user
        const channels = group.channelArr
          .filter(
            (ch) =>
              ch.features.includes('feed') &&
              ch.featureSettings.feed.allowMemberPosts,
          )
          .filter((ch) => !isChannelLocked(ch.mangoArr, ch.badgeArr));

        accessChannelsToPost.push(...channels.map((ch) => ch));

        return acc || channels.length > 0;
      }, false);

    const accessChannels = groupChannelList.reduce((acc, group) => {
      const channels = hasCommunityCreatorAccess
        ? group.channelArr
        : group.channelArr.filter(
            (ch) => !isChannelLocked(ch.mangoArr, ch.badgeArr),
          );

      return [...acc, ...channels];
    }, [] as IChannel[]);

    // check TQ Connect access for user
    // if user has any of mangoes enabled for TQ Connect, then they have TQ Connect access
    const hasTQConnectAccessForUser =
      Boolean(hostMetadata?.tqConnectConfig?.enabled) &&
      subscribedMangoes.some((m) =>
        hostMetadata?.tqConnectConfig?.allowedMangoes?.includes(m._id),
      );

    dispatch(
      setUserCommunityAccess({
        communityCreator: hostMetadata.creator._id || '',
        hasCommunityCreatorAccess,
        canPostInMangoes,
        canPostInChannels,
        accessChannelsToPost: hasCommunityCreatorAccess
          ? accessChannels
          : accessChannelsToPost,
        accessChannels,
        accessMangoes: hasCommunityCreatorAccess
          ? allCreatorMangoes
          : subscribedMangoes,
        accessBadges: hasCommunityCreatorAccess
          ? allBadges
          : allBadges.filter((ab) => userBadges?.some((b) => b === ab._id)),
        hasTQConnectAccessForUser,
      }),
    );
  };

/**
 * Uploads files in community handler
 * @param files - files to upload
 * @param loadingCallback - callback to set loading state
 * @param onUploadProgress - callback to get upload progress
 * @returns uploaded files
 */
export const uploadFilesForCommunity =
  (
    files: File[],
    loadingCallback?: (loading: boolean) => void,
    onUploadProgress?: (e: AxiosProgressEvent) => void,
    uploadKey?: EUploadModuleType,
  ): AppThunk<Promise<ICommunityUploadedMedia[] | undefined>> =>
  async (): Promise<ICommunityUploadedMedia[] | undefined> => {
    loadingCallback?.(true);
    try {
      const formData = new FormData();

      files.forEach((file) => formData.append('file', file));

      const resp = await CommunityAPI.uploadCommunityMedia(
        files,
        onUploadProgress || (() => {}),
        uploadKey!,
      );

      if ([201, 200].includes(resp.status)) {
        const uploadedFiles = resp.data.result.assets?.map((file) => ({
          _id: file.assetId,
          type: getGenericFileType(file.fileName) || file.fileType,
          contentUrl: file.url,
          filename: file.fileName,
          path: file.url,
          assetUrl: file.url,
        }));
        return uploadedFiles;
      }
      return undefined;
    } catch (error) {
      showAppError(error);
      throw error;
    } finally {
      loadingCallback?.(false);
    }
  };
